import React from 'react';
import PropTypes from 'prop-types';
import {
  Select as MuiSelect,
  MenuItem,
  FormControl,
  FormLabel,
  FormHelperText,
  styled,
} from '@mui/material';

// Modern styled select with consistent design across the app
const StyledSelect = styled(MuiSelect)(({ theme }) => ({
  height: '48px',
  borderRadius: '12px',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.02)',
  transition: theme.transitions.create([
    'border-color',
    'background-color',
    'box-shadow',
  ]),

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
  },

  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: 'transparent',
    top: 0,
    legend: {
      display: 'none',
    },
  },

  '&.Mui-focused': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1px',
    },
  },

  // Disabled state
  '&.Mui-disabled': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.04)',
    opacity: 0.7,
  },

  // Error state
  '&.Mui-error': {
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.error.main,
    },
  },
}));

// Styled label to match TextField
const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  transform: 'none',
  position: 'relative',
  marginBottom: theme.spacing(1),
  '&.Mui-focused': {
    color: theme.palette.text.secondary,
  },
  '&.Mui-error': {
    color: theme.palette.error.main,
  },
}));

// Styled MenuItem for consistent dropdown options
const StyledMenuItem = styled(MenuItem)(({ theme }) => ({
  height: '44px',
  fontSize: '0.875rem',
  padding: '0 16px',
  display: 'flex',
  alignItems: 'center',
  transition: theme.transitions.create(['background-color']),
  borderRadius: '8px',
  margin: '2px 0',

  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.08)'
        : 'rgba(0, 0, 0, 0.04)',
  },

  '&.Mui-selected': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.06)',
    '&:hover': {
      backgroundColor:
        theme.palette.mode === 'dark'
          ? 'rgba(255, 255, 255, 0.16)'
          : 'rgba(0, 0, 0, 0.08)',
    },
  },

  '&.Mui-disabled': {
    opacity: 0.5,
  },
}));

// Select component with improved styling and ref forwarding
const Select = React.forwardRef(
  (
    {
      options = [],
      label,
      value,
      onChange,
      name,
      error = false,
      helperText,
      fullWidth = true,
      disabled = false,
      required = false,
      children,
      ...props
    },
    ref
  ) => {
    return (
      <FormControl fullWidth={fullWidth} error={error} disabled={disabled}>
        {label && (
          <StyledFormLabel required={required}>{label}</StyledFormLabel>
        )}
        <StyledSelect
          labelId={`${name}-label`}
          id={name}
          value={value}
          onChange={onChange}
          variant="outlined"
          ref={ref}
          MenuProps={{
            PaperProps: {
              sx: {
                mt: 1,
                borderRadius: '12px',
                border: '1px solid',
                borderColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.1)',
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 4px 20px rgba(0, 0, 0, 0.5)'
                    : '0 4px 20px rgba(0, 0, 0, 0.15)',
                '& .MuiList-root': {
                  padding: '6px',
                },
              },
            },
          }}
          {...props}
        >
          {/* Support both options array and MenuItem children */}
          {options && options.length > 0
            ? options.map((option) => (
                <StyledMenuItem key={option.value} value={option.value}>
                  {option.label}
                </StyledMenuItem>
              ))
            : React.Children.map(children, (child) => {
                if (React.isValidElement(child) && child.type === MenuItem) {
                  return <StyledMenuItem {...child.props} />;
                }
                return child;
              })}
        </StyledSelect>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }
);

Select.displayName = 'Select';

Select.propTypes = {
  /**
   * Array of options for the select.
   * Each option should have a value and a label.
   */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.any.isRequired,
      label: PropTypes.node.isRequired,
    })
  ),
  /**
   * The label of the select.
   */
  label: PropTypes.string,
  /**
   * The value of the select.
   */
  value: PropTypes.any,
  /**
   * Callback fired when the value is changed.
   */
  onChange: PropTypes.func,
  /**
   * The name of the select.
   */
  name: PropTypes.string,
  /**
   * If true, the select will display an error state.
   */
  error: PropTypes.bool,
  /**
   * The helper text to display.
   */
  helperText: PropTypes.node,
  /**
   * If true, the select will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * If true, the select will be disabled.
   */
  disabled: PropTypes.bool,
  /**
   * If true, the label will be displayed as required.
   */
  required: PropTypes.bool,
  /**
   * The content of the component.
   */
  children: PropTypes.node,
};

export default Select;
